import './index.scss';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Toast from '../../shared/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../../../store/Auth/authHooks';
import {
  useFetchSpacesManagementSpacesByCompany,
  useSpacesManagementCompanySpaces,
} from '../../../store/SpacesManagement/spacesManagementHooks';
import {
  useGuestsManagementAddNewGuest,
  useGuestsManagementAddNewGuestFromGuard,
} from '../../../store/guests-management/guestsManagementHooks';
import { InviteGuestForm } from '../../pages/my-guests/InviteGuestForm';
import {
  useCloseOldModal,
  useModalData,
} from '../../../store/modal/modalHooks';
import { Button, Form, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { SpecialNeedEnum } from '../../../API/guest-management/enum/special-need.enum';

export interface InviteVisitorFormValues {
  fullName: string;
  phone: string;
  email: string;
  arrivingOn: Date[];
  companyId: string;
  building: number;
  allowParking: boolean;
  licenseNumber: string;
  message: string;
  parkingNumber: string;
  parkingFloorNumber: string;
  guestFloors: string[];
  orderForAnother: boolean;
  invitedName: string;
  invitedNameFreeText: string;
  specialNeed?: SpecialNeedEnum;
  space: number;
}

const InviteVisitorModal = () => {
  const profile = useProfile();
  const { t } = useTranslation();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const inviteGuest = useGuestsManagementAddNewGuest();
  const companySpaces = useSpacesManagementCompanySpaces();
  const fetchCompanySpaces = useFetchSpacesManagementSpacesByCompany();
  const inviteGuestFromGuard = useGuestsManagementAddNewGuestFromGuard();

  useEffect(() => {
    fetchCompanySpaces().finally();
  }, [profile?.company?.id!]);

  const spaces = [
    ...new Set(
      companySpaces.map(
        (space) => `${space.building.name}, ${space.floor?.number}`
      )
    ),
  ];

  const defaultValues: InviteVisitorFormValues = {
    fullName: '',
    phone: '',
    email: '',
    arrivingOn: [],
    companyId: profile?.companyId?.toString() || '',
    building: profile?.company?.buildings
      ? profile?.company?.buildings[0]?.id
      : 0,
    allowParking: false,
    licenseNumber: '',
    message: '',
    parkingNumber: '',
    parkingFloorNumber: '',
    guestFloors: [],
    orderForAnother: false,
    invitedName: '',
    invitedNameFreeText: '',
    specialNeed: SpecialNeedEnum.NORMAL,
    space: companySpaces?.[0]?.id,
  };

  const form = useForm({ defaultValues });
  const {
    handleSubmit,
    formState: {},
  } = form;

  useEffect(() => {
    if (companySpaces.length) {
      form.setValue('space', companySpaces[0].id);
    }
  }, [companySpaces]);

  const onSubmit = (data: InviteVisitorFormValues) => {
    const newData = {
      ...data,
      guestFloors: spaces,
    };

    const guestSpace = companySpaces.find((space) => space.id === data.space);

    const space = {
      location_id: guestSpace?.building.locationId || -1,
      building_id: guestSpace?.building.id || -1,
      floor_id: guestSpace?.floorId || -1,
    };
    if (modalData?.data?.isGuard) {
      inviteGuestFromGuard(
        newData,
        space,
        (isSuccessful, message) => {
          const toastType = isSuccessful ? 'success-request' : 'error';
          Toast(toastType, message, t);
        },
        t
      )
        .catch((error) => {
          Toast(
            'error',
            error.response.data?.error?.message || error.response.data?.message
          );
        })
        .finally(() => {
          closeModal();
        });
    } else {
      inviteGuest(
        newData,
        space,
        (isSuccessful, message) => {
          const toastType = isSuccessful ? 'success-request' : 'error';
          Toast(toastType, message, t);
        },
        t
      )
        .catch((error) => {
          Toast(
            'error',
            error.response.data?.error?.message || error.response.data
          );
        })
        .finally(() => {
          closeModal();
        });
    }
  };

  return (
    <div className="invite-new-guest-modal">
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <p className="form-header-title">{t('INVITE_NEW_VISITOR')}</p>
          </ModalHeader>
          <ModalBody>
            <InviteGuestForm form={form} />
          </ModalBody>
          <ModalFooter className="invite-new-guest-modal-buttons">
            <Button type="button" color="" onClick={closeModal}>
              {t('CLOSE_BUTTON')}
            </Button>{' '}
            <Button type="submit" color="primary">
              {t('INVITE')}
            </Button>{' '}
          </ModalFooter>
        </Form>
      </div>
    </div>
  );
};

export default InviteVisitorModal;
